import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  Building,
  CheckCircle,
  PauseCircle,
  Play,
  GitGraph,
  ExternalLink,
  ShieldAlert,
  Activity,
  Loader2,
  Eye,
  FileCode,
  Globe,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-hot-toast";
import Tooltip from "../../widgets/ToolTip";
import tenantService, { Tenant, TenantMetrics, CloudFlareDnsRecord, TenantDnsRawRecord, TenantIngressHistory } from "../../services/tenant.service";
import YAMLViewerModal from "../../components/tenants/YAMLViewerModal";
import IngressYAMLEditor from "../../components/tenants/IngressYAMLEditor";
import DNSManagerModal from "../../components/tenants/DNSManagerModal";
import IngressHistoryModal from "../../components/tenants/IngressHistoryModal";
import ConfirmationDialog from "../../widgets/ConfirmationModal";

const PAGE_SIZE = 10;

const normalizeTenantDnsRecord = (r: TenantDnsRawRecord): CloudFlareDnsRecord => ({
  id: r.record_id,
  name: r.hostname,
  type: r.type,
  content: r.target,
  proxied: r.proxied,
  created_on: r.created_at,
});

function Tenants() {
  const navigate = useNavigate();

  // -------- Tenant list state --------
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [metrics, setMetrics] = useState<TenantMetrics>({ total: 0, active: 0, inactive: 0, suspended: 0 });
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const searchDebounce = useRef<ReturnType<typeof setTimeout>>();

  // -------- Global YAML state --------
  const [yamlViewerOpen, setYamlViewerOpen] = useState(false);
  const [yamlEditorOpen, setYamlEditorOpen] = useState(false);
  const [yamlContent, setYamlContent] = useState("");
  const [yamlLoading, setYamlLoading] = useState(false);

  // -------- Global DNS state --------
  const [globalDnsModalOpen, setGlobalDnsModalOpen] = useState(false);
  const [globalDnsRecords, setGlobalDnsRecords] = useState<CloudFlareDnsRecord[]>([]);
  const [globalDnsLoading, setGlobalDnsLoading] = useState(false);

  // -------- Per-tenant ingress history state --------
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyTenant, setHistoryTenant] = useState<Tenant | null>(null);
  const [historyData, setHistoryData] = useState<TenantIngressHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // -------- Per-tenant DNS state --------
  const [tenantDnsModalOpen, setTenantDnsModalOpen] = useState(false);
  const [tenantDnsRecords, setTenantDnsRecords] = useState<CloudFlareDnsRecord[]>([]);
  const [tenantDnsLoading, setTenantDnsLoading] = useState(false);
  const [currentDnsTenant, setCurrentDnsTenant] = useState<Tenant | null>(null);

  // -------- Per-tenant sync state --------
  const [syncingKey, setSyncingKey] = useState<string | null>(null);

  // -------- Delete confirmation state --------
  const [deleteTarget, setDeleteTarget] = useState<Tenant | null>(null);

  // -------- Data fetching --------

  const fetchTenants = useCallback(async (page: number, search: string) => {
    setLoading(true);
    try {
      const res = await tenantService.listTenants({
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        search: search || undefined,
      });
      setTenants(res.data.tenants);
      setTotalCount(res.data.total_count);
    } catch {
      toast.error("Failed to load tenants");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await tenantService.getMetrics();
      setMetrics(res.data);
    } catch {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    fetchTenants(currentPage, searchTerm);
  }, [currentPage]);

  useEffect(() => {
    fetchMetrics();
  }, []);

  // -------- Global YAML handlers --------

  const handleViewYAML = async () => {
    setYamlLoading(true);
    setYamlViewerOpen(true);
    try {
      const res = await tenantService.getIngressYAML();
      setYamlContent(res.data.yaml);
    } catch {
      toast.error("Failed to fetch ingress YAML");
      setYamlViewerOpen(false);
    } finally {
      setYamlLoading(false);
    }
  };

  const handleEditYAML = async () => {
    setYamlLoading(true);
    setYamlEditorOpen(true);
    try {
      const res = await tenantService.getIngressYAML();
      setYamlContent(res.data.yaml);
    } catch {
      toast.error("Failed to fetch ingress YAML");
      setYamlEditorOpen(false);
    } finally {
      setYamlLoading(false);
    }
  };

  const handleSaveYAML = async (yaml: string, commitMessage: string): Promise<void> => {
    const res = await tenantService.updateIngressYAML({ yaml, commit_message: commitMessage });
    setYamlEditorOpen(false);
    toast.success(`YAML committed: ${res.data.commit_id.slice(0, 8)}`);
  };

  // -------- Global DNS handlers --------

  const handleManageDNS = async () => {
    setGlobalDnsLoading(true);
    setGlobalDnsModalOpen(true);
    try {
      const res = await tenantService.getAllDNSRecords();
      setGlobalDnsRecords(res.data.records);
    } catch {
      toast.error("Failed to fetch DNS records");
      setGlobalDnsModalOpen(false);
    } finally {
      setGlobalDnsLoading(false);
    }
  };

  const handleRefreshGlobalDNS = async () => {
    setGlobalDnsLoading(true);
    try {
      const res = await tenantService.getAllDNSRecords();
      setGlobalDnsRecords(res.data.records);
    } catch {
      toast.error("Failed to refresh DNS records");
    } finally {
      setGlobalDnsLoading(false);
    }
  };

  // -------- Per-tenant handlers --------

  const handleViewHistory = async (tenant: Tenant) => {
    setHistoryTenant(tenant);
    setHistoryLoading(true);
    setHistoryModalOpen(true);
    try {
      const res = await tenantService.getIngressHistory(tenant.tenant_key);
      setHistoryData(res.data.history);
    } catch {
      toast.error("Failed to fetch ingress history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleViewTenantDNS = async (tenant: Tenant) => {
    setCurrentDnsTenant(tenant);
    setTenantDnsLoading(true);
    setTenantDnsModalOpen(true);
    try {
      const res = await tenantService.getTenantDNSRecords(tenant.tenant_key);
      setTenantDnsRecords(res.data.records.map(normalizeTenantDnsRecord));
    } catch {
      toast.error("Failed to fetch DNS records");
      setTenantDnsModalOpen(false);
    } finally {
      setTenantDnsLoading(false);
    }
  };

  const handleRefreshTenantDNS = async () => {
    if (!currentDnsTenant) return;
    setTenantDnsLoading(true);
    try {
      const res = await tenantService.getTenantDNSRecords(currentDnsTenant.tenant_key);
      setTenantDnsRecords(res.data.records.map(normalizeTenantDnsRecord));
    } catch {
      toast.error("Failed to refresh DNS records");
    } finally {
      setTenantDnsLoading(false);
    }
  };

  const handleSyncIngress = async (tenant: Tenant) => {
    setSyncingKey(tenant.tenant_key);
    try {
      // TODO: replace with tenantService.syncTenantIngress(tenant.tenant_key)
      await new Promise((r) => setTimeout(r, 1000));
      toast.success(`Ingress sync triggered for ${tenant.name}`);
      fetchTenants(currentPage, searchTerm);
    } catch {
      toast.error("Failed to sync ingress");
    } finally {
      setSyncingKey(null);
    }
  };

  // -------- Search & pagination --------

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      fetchTenants(1, value);
    }, 400);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // -------- Tenant CRUD actions --------

  const handleEditTenant = (tenant: Tenant) => {
    navigate(`/enterprise/tenants/edit/${tenant.tenant_key}`);
  };

  const handleDeleteTenant = (tenant: Tenant) => {
    setDeleteTarget(tenant);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await tenantService.deleteTenant(deleteTarget.tenant_key);
      toast.success("Tenant deleted");
      setDeleteTarget(null);
      fetchTenants(currentPage, searchTerm);
      fetchMetrics();
    } catch {
      toast.error("Failed to delete tenant");
    }
  };

  const handleToggleStatus = async (tenant: Tenant) => {
    const newStatus = (tenant.status ?? "INACTIVE") === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    try {
      await tenantService.updateTenantStatus(tenant.tenant_key, newStatus);
      toast.success(`Tenant ${newStatus === "ACTIVE" ? "activated" : "suspended"}`);
      fetchTenants(currentPage, searchTerm);
      fetchMetrics();
    } catch {
      toast.error("Failed to update tenant status");
    }
  };

  // -------- Badge helpers --------

  const getGitlabStatus = (tenant: Tenant): "synced" | "unsynced" | "pending" => {
    const s = tenant.integrations?.gitlab_ingress?.status;
    if (!s) return "pending";
    return s === "success" ? "synced" : "unsynced";
  };

  const getDnsCount = (tenant: Tenant) =>
    tenant.integrations?.cloudflare_dns?.records?.length ?? 0;

  const getStatusBadge = (status: Tenant["status"]) => {
    const normalized = (status ?? "INACTIVE").toUpperCase() as Tenant["status"];
    const styles: Record<Tenant["status"], string> = {
      ACTIVE: "bg-green-50 text-green-700 border-green-200",
      SUSPENDED: "bg-amber-50 text-amber-700 border-amber-200",
      INACTIVE: "bg-slate-50 text-slate-700 border-slate-200",
    };
    const dotColor: Record<Tenant["status"], string> = {
      ACTIVE: "bg-green-500",
      SUSPENDED: "bg-amber-500",
      INACTIVE: "bg-slate-400",
    };
    const safeStatus: Tenant["status"] = styles[normalized] ? normalized : "INACTIVE";
    const label = safeStatus.charAt(0) + safeStatus.slice(1).toLowerCase();
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full border ${styles[safeStatus]}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${dotColor[safeStatus]}`} />
        {label}
      </span>
    );
  };

  const getGitStatusBadge = (status: "synced" | "unsynced" | "pending") => {
    const styles = {
      synced: "bg-emerald-50 text-emerald-700 border-emerald-100",
      pending: "bg-blue-50 text-blue-700 border-blue-100",
      unsynced: "bg-rose-50 text-rose-700 border-rose-100",
    };
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded border ${styles[status]}`}>
        <GitGraph className="w-3 h-3" />
        GitLab: {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // -------- Render --------

  return (
    <div className="min-h-screen bg-gray-50">
      <div>
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Tenants</h1>
                <p className="text-xs text-slate-600">Manage your organization tenants</p>
              </div>
              <div className="flex flex-wrap gap-2 items-center justify-end">
                {/* Search */}
                <div className="relative w-56">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search tenants..."
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>

                {/* Global cluster buttons */}
                <button
                  onClick={handleViewYAML}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
                >
                  <Eye className="w-4 h-4" />
                  View YAML
                </button>
                <button
                  onClick={handleEditYAML}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
                >
                  <FileCode className="w-4 h-4" />
                  Edit YAML
                </button>
                <button
                  onClick={handleManageDNS}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
                >
                  <Globe className="w-4 h-4" />
                  Manage DNS
                </button>

                {/* Add Tenant */}
                <button
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  onClick={() => navigate("/enterprise/tenants/create")}
                  type="button"
                >
                  <Plus className="w-4 h-4" />
                  Add Tenant
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Tenants</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{metrics.total}</p>
                <p className="text-xs text-gray-400 mt-1">All tenants in platform</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <Building className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Active Tenants</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{metrics.active}</p>
                <p className="text-xs text-gray-400 mt-1">Currently operating</p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Suspended Tenants</p>
                <p className="text-3xl font-bold text-yellow-600 mt-1">{metrics.suspended}</p>
                <p className="text-xs text-gray-400 mt-1">Paused or on hold</p>
              </div>
              <div className="w-12 h-12 bg-yellow-50 rounded-lg flex items-center justify-center">
                <PauseCircle className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tenant List */}
        <div className="space-y-4">
          {loading ? (
            <div className="bg-white rounded-xl border border-slate-200 py-20 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : tenants.length === 0 ? (
            <div className="bg-white rounded-xl border border-dashed border-slate-300 py-20 text-center">
              <Building className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-medium">No tenants found</p>
              <button
                className="mt-4 text-blue-600 font-medium text-sm hover:underline"
                onClick={() => navigate("/enterprise/tenants/create")}
              >
                Create your first tenant
              </button>
            </div>
          ) : (
            tenants.map((tenant) => {
              const gitStatus = getGitlabStatus(tenant);
              const dnsCount = getDnsCount(tenant);
              const adminUrl = tenant.domains?.[0]?.admin_url || "";
              const apiUrl = tenant.domains?.[0]?.api_url || "";
              const isSyncing = syncingKey === tenant.tenant_key;

              return (
                <div
                  key={tenant._id}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-blue-300 hover:shadow-sm transition-all"
                >
                  <div className="p-5 flex flex-col lg:flex-row lg:items-center gap-6">
                    {/* Left: Branding & Core Info */}
                    <div className="flex items-start gap-4 lg:w-1/4">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm shadow-blue-200">
                        <Building className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-base font-bold text-slate-900 truncate">{tenant.name}</h3>
                          {getStatusBadge(tenant.status)}
                        </div>
                        <p className="text-xs text-slate-400 font-mono">{tenant.tenant_key}</p>
                        {(tenant.business_types?.length ?? 0) > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {tenant.business_types!.map((tag) => (
                              <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-bold rounded uppercase tracking-wider">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Middle: Clickable integration badges */}
                    <div className="flex flex-wrap gap-4 lg:flex-1 lg:justify-center items-center">
                      <div className="flex flex-col gap-2">
                        {/* GitLab badge → opens IngressHistoryModal */}
                        <button
                          onClick={() => handleViewHistory(tenant)}
                          className="hover:opacity-75 transition-opacity text-left"
                          title="View ingress sync history"
                        >
                          {getGitStatusBadge(gitStatus)}
                        </button>

                        {/* DNS badge → opens per-tenant DNSManagerModal */}
                        <button
                          onClick={() => handleViewTenantDNS(tenant)}
                          className="flex items-center gap-2 px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded text-[11px] font-medium w-fit hover:bg-indigo-100 transition-colors"
                          title="View DNS records"
                        >
                          <Activity className="w-3 h-3" />
                          DNS: {dnsCount} Records
                        </button>

                        {/* Sync button — only when failed or pending */}
                        {(gitStatus === "unsynced" || gitStatus === "pending") && (
                          <button
                            onClick={() => handleSyncIngress(tenant)}
                            disabled={isSyncing}
                            className="flex items-center gap-1.5 px-2 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded text-[11px] font-medium w-fit hover:bg-rose-100 transition-colors disabled:opacity-50"
                          >
                            {isSyncing ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3 h-3" />
                            )}
                            Sync Ingress
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Right: Endpoints */}
                    <div className="lg:w-1/3 flex flex-col gap-3">
                      {adminUrl && (
                        <div className="flex items-center justify-between group">
                          <div className="flex flex-col">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Admin URL</span>
                            <a href={adminUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 truncate">
                              {adminUrl}
                              <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </a>
                          </div>
                        </div>
                      )}
                      {apiUrl && (
                        <div className="flex items-center justify-between group">
                          <div className="flex flex-col">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">API URL</span>
                            <a href={apiUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-slate-600 hover:text-slate-800 flex items-center gap-1 truncate">
                              {apiUrl}
                              <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </a>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-4 lg:pt-0 lg:border-l lg:pl-6 border-slate-100">
                      <Tooltip content="Edit Settings">
                        <button
                          onClick={() => handleEditTenant(tenant)}
                          className="w-9 h-9 flex items-center justify-center bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm shadow-blue-100"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content={tenant.status === "ACTIVE" ? "Suspend Tenant" : "Activate Tenant"}>
                        <button
                          onClick={() => handleToggleStatus(tenant)}
                          className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all shadow-sm ${
                            tenant.status === "ACTIVE"
                              ? "bg-amber-50 text-amber-600 hover:bg-amber-600 hover:text-white shadow-amber-100"
                              : "bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white shadow-emerald-100"
                          }`}
                        >
                          {tenant.status === "ACTIVE" ? <ShieldAlert className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                      </Tooltip>
                      <Tooltip content="Delete Permanent">
                        <button
                          onClick={() => handleDeleteTenant(tenant)}
                          className="w-9 h-9 flex items-center justify-center bg-rose-50 text-rose-600 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm shadow-rose-100"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 bg-white rounded-md border border-slate-200 shadow-sm px-5 py-3 flex items-center justify-between">
            <div className="text-xs text-slate-600 font-medium">
              Showing{" "}
              <span className="font-semibold text-slate-900">{(currentPage - 1) * PAGE_SIZE + 1}</span>
              {" "}to{" "}
              <span className="font-semibold text-slate-900">{Math.min(currentPage * PAGE_SIZE, totalCount)}</span>
              {" "}of{" "}
              <span className="font-semibold text-slate-900">{totalCount}</span> tenants
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-md text-xs font-semibold transition-all ${
                      currentPage === pageNum
                        ? "bg-blue-600 text-white shadow-sm shadow-blue-200"
                        : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ===== Modals ===== */}

      {/* Global: read-only YAML viewer */}
      <YAMLViewerModal
        isOpen={yamlViewerOpen}
        title="Cluster Ingress YAML (Global)"
        yaml={yamlContent}
        loading={yamlLoading}
        onClose={() => setYamlViewerOpen(false)}
      />

      {/* Global: YAML editor with commit */}
      <IngressYAMLEditor
        isOpen={yamlEditorOpen}
        initialYAML={yamlContent}
        tenantKey="global"
        onClose={() => setYamlEditorOpen(false)}
        onSuccess={handleSaveYAML}
      />

      {/* Global: CloudFlare DNS manager */}
      <DNSManagerModal
        isOpen={globalDnsModalOpen}
        title="Global CloudFlare DNS Records"
        tenantKey="global"
        records={globalDnsRecords}
        loading={globalDnsLoading}
        onClose={() => setGlobalDnsModalOpen(false)}
        onRefresh={handleRefreshGlobalDNS}
      />

      {/* Per-tenant: ingress sync history */}
      <IngressHistoryModal
        isOpen={historyModalOpen}
        tenantName={historyTenant?.name ?? ""}
        tenantKey={historyTenant?.tenant_key ?? ""}
        history={historyData}
        loading={historyLoading}
        onClose={() => { setHistoryModalOpen(false); setHistoryTenant(null); setHistoryData([]); }}
      />

      {/* Per-tenant: DNS records manager — refresh scoped to this tenant */}
      <DNSManagerModal
        isOpen={tenantDnsModalOpen}
        title={`DNS Records — ${currentDnsTenant?.name ?? ""}`}
        tenantKey={currentDnsTenant?.tenant_key ?? ""}
        records={tenantDnsRecords}
        loading={tenantDnsLoading}
        onClose={() => { setTenantDnsModalOpen(false); setCurrentDnsTenant(null); }}
        onRefresh={handleRefreshTenantDNS}
      />

      {/* Delete tenant confirmation */}
      <ConfirmationDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        iconName="delete"
        title="Delete Tenant"
        description={`Are you sure you want to delete <strong>${deleteTarget?.name}</strong>? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
}

export default Tenants;
